import { useTranslation } from "react-i18next"

export interface OnboardingProgressProps {
  /** 1-indexed current step (`null` on terminal `identity_mismatch`). */
  step: number | null
  /** Total steps in the flow (always 4 for OKS-22 v1). */
  total: number
  className?: string
}

/**
 * OnboardingProgress — top-of-screen step indicator.
 *
 * Pencil prototype `TCPu0`/`cTDGw`/`bjCIi` shows the format **"X of N"**
 * centered above the step heading (NOT stepper dots — UX Spec v3 amended
 * the v1 dots in favour of the prototype's text format).
 *
 * Renders nothing when `step === null` (terminal `identity_mismatch` /
 * `connect_failure` states have no step in the flow).
 *
 * Accessibility:
 *   - Outer `<nav aria-label="…">` carries the long-form announcement
 *     ("Step X of N") via i18n key `signup.progress.aria_label`.
 *   - Visible label uses the short-form "X of N" per prototype.
 *   - The visual segment row carries `aria-hidden` so screen readers
 *     read the structured label, not the visual dot row.
 */
export function OnboardingProgress({
  step,
  total,
  className = "",
}: OnboardingProgressProps) {
  const { t } = useTranslation()
  if (step === null || step < 1 || step > total) return null
  return (
    <nav
      aria-label={t("signup.progress.aria_label", { step, total })}
      className={`flex flex-col items-center gap-2 ${className}`}
      data-testid="onboarding-progress"
    >
      <span
        className="text-muted-foreground text-sm font-medium tabular-nums"
        data-testid="onboarding-progress-label"
      >
        {t("signup.progress.label", { step, total })}
      </span>
      {/* Visual segment row — decorative; the screen-reader announcement is
          on the parent <nav>. Each segment carries data-active /
          data-completed for QA Playwright + Vitest selectors. */}
      <ol aria-hidden="true" className="flex items-center gap-1.5">
        {Array.from({ length: total }, (_, i) => i + 1).map((segment) => {
          const isActive = segment === step
          const isCompleted = segment < step
          return (
            <li
              key={segment}
              data-testid={`onboarding-progress-${segment}`}
              data-active={isActive ? "true" : undefined}
              data-completed={isCompleted ? "true" : undefined}
              className={
                isActive
                  ? "bg-foreground h-1.5 w-6 rounded-full transition-all"
                  : isCompleted
                    ? "bg-foreground/60 h-1.5 w-3 rounded-full transition-all"
                    : "bg-foreground/20 h-1.5 w-3 rounded-full transition-all"
              }
            />
          )
        })}
      </ol>
    </nav>
  )
}

export default OnboardingProgress
